import React, { useState, useMemo, useRef } from 'react';
import emailjs from '@emailjs/browser';
import {
  STATUTS_ADHESION, MODES_PAIEMENT, JOURS_PRELEVEMENT, PERIODICITES,
  CONTRATS, CONVENTIONS, MANDATS, MOIS, STATUS_LABELS, STATUS_BADGE_CLASS,
  initials, formatMontant, formatDate, anciennete, uid, exportCSV
} from '../utils/helpers';

const EJS_SERVICE = process.env.REACT_APP_EMAILJS_SERVICE_ID        || '';
const EJS_NOTIF   = process.env.REACT_APP_EMAILJS_NOTIF_TEMPLATE_ID || '';
const EJS_KEY     = process.env.REACT_APP_EMAILJS_PUBLIC_KEY         || '';

function generateRUM(nom, seq) {
  const year = new Date().getFullYear();
  const letters = (nom || 'XXX').replace(/[^A-Z]/gi,'').toUpperCase().slice(0,3).padEnd(3,'X');
  return `SDASSP-${year}-${letters}-${String(seq).padStart(3,'0')}`;
}

const EMPTY_ADH = {
  id: '', genre: '', prenom: '', nom: '', numero: '', statut: 'Actif',
  etablissementId: '', metier: '', contrat: 'CDI', dateEntree: '',
  convention: 'CCN 51', coefficient: '', dateAdhesion: '',
  modePaiement: 'Prélèvement auto', jourPrelevement: '5',
  periodicite: 'Mensuelle', moisEcheanceAnnuelle: null, montant: '', rib: '', bic: '',
  telephone: '', email: '', adresse: '', notes: '',
  delegueNom: '', createdBy: '', documents: [], mandats: [],
};

const METIERS_SUGGESTIONS = [
  'Aide-soignant(e)','Infirmier(ère)','Éducateur(rice) spécialisé(e)','Aide médico-psychologique',
  'Accompagnant éducatif et social','Psychologue','Assistant(e) de service social','Chef de service',
  'Animateur(rice)','Agent de service hospitalier','Moniteur(rice) éducateur(rice)',
  'Médecin','Kinésithérapeute','Ergothérapeute','Orthophoniste','Diététicien(ne)',
  'Secrétaire médicale','Responsable hébergement','Cuisinier(ère)','Agent de maintenance',
];

export default function FicheAdherent({
  adherents, etablissements, cotisations, selectedYear,
  saveAdherent, deleteAdherent, saveEtablissement,
  selectedId, setSelectedId, goToEtab, addJournal, currentUsername,
  saveDemande, demandes, currentRole, users
}) {
  const [activeTab, setActiveTab] = useState('emploi');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(EMPTY_ADH);
  const [creating, setCreating] = useState(false);
  const [filterSearch, setFilterSearch] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [filterEtab, setFilterEtab] = useState('');
  const [filterPay, setFilterPay] = useState('');
  const [showNewEtab, setShowNewEtab] = useState(false);
  const [newEtabNom, setNewEtabNom] = useState('');
  const [metierSearch, setMetierSearch] = useState('');
  const [metierOpen, setMetierOpen] = useState(false);
  const [importError, setImportError] = useState('');
  const [mandatPopover, setMandatPopover] = useState(null); // adhérent id
  const [demandeModal, setDemandeModal] = useState(null); // 'arret' | 'rib' | 'bascule'
  const [demandeNotes, setDemandeNotes] = useState('');
  const [newIban, setNewIban] = useState('');
  const [newBic, setNewBic] = useState('');
  const [basculeIban, setBasculeIban] = useState('');
  const [basculeBic, setBasculeBic] = useState('');
  const [basculeTitulaire, setBasculeTitulaire] = useState('');
  const [basculeJour, setBasculeJour] = useState('5');
  const [basculePeriodicite, setBasculePeriodicite] = useState('Mensuelle');
  const [basculeSepa, setBasculeSepa] = useState(null);
  const [basculeRum, setBasculeRum] = useState('');
  const [ribDoc, setRibDoc] = useState(null);
  const fileRef = useRef();
  const docRef = useRef();
  const sepaRef = useRef();
  const ribDocRef = useRef();

  const notifAdmins = (sujet, message_body) => {
    if (!EJS_SERVICE || !EJS_NOTIF || !EJS_KEY) return;
    (users || []).filter(u => u.isAdmin && u.email).forEach(admin => {
      emailjs.send(EJS_SERVICE, EJS_NOTIF, {
        to_email: admin.email, destinataire_name: admin.username, sujet, message_body,
      }, EJS_KEY).catch(() => {});
    });
  };

  const selected = adherents.find(a => a.id === selectedId);

  const PAY_OPTIONS = [
    { id: '',           label: 'Tous modes' },
    { id: 'prelev_1',   label: 'Prél. J1',    test: a => a.modePaiement === 'Prélèvement auto' && (!a.periodicite || a.periodicite === 'Mensuelle') && String(a.jourPrelevement) === '1' },
    { id: 'prelev_3',   label: 'Prél. J3',    test: a => a.modePaiement === 'Prélèvement auto' && (!a.periodicite || a.periodicite === 'Mensuelle') && String(a.jourPrelevement) === '3' },
    { id: 'prelev_5',   label: 'Prél. J5',    test: a => a.modePaiement === 'Prélèvement auto' && (!a.periodicite || a.periodicite === 'Mensuelle') && String(a.jourPrelevement) === '5' },
    { id: 'prelev_10',  label: 'Prél. J10',   test: a => a.modePaiement === 'Prélèvement auto' && (!a.periodicite || a.periodicite === 'Mensuelle') && String(a.jourPrelevement) === '10' },
    { id: 'prelev_30',  label: 'Prél. J30',   test: a => a.modePaiement === 'Prélèvement auto' && (!a.periodicite || a.periodicite === 'Mensuelle') && String(a.jourPrelevement) === '30' },
    { id: 'trimestre',  label: 'Trimestriel', test: a => a.modePaiement === 'Prélèvement auto' && a.periodicite === 'Trimestrielle' },
    { id: 'semestre',   label: 'Semestriel',  test: a => a.modePaiement === 'Prélèvement auto' && a.periodicite === 'Semestrielle' },
    { id: 'annuel_per', label: 'Annuel',      test: a => a.modePaiement === 'Prélèvement auto' && a.periodicite === 'Annuelle' },
    { id: 'virement',   label: 'Virement',    test: a => a.modePaiement === 'Virement' },
    { id: 'cheque',     label: 'Chèque',      test: a => a.modePaiement === 'Chèque' },
  ];
  const currentPay = PAY_OPTIONS.find(p => p.id === filterPay) || PAY_OPTIONS[0];

  const filtered = useMemo(() => {
    return adherents.filter(adh => {
      if (filterStatut && adh.statut !== filterStatut) return false;
      if (filterEtab && adh.etablissementId !== filterEtab) return false;
      if (filterPay && currentPay.test && !currentPay.test(adh)) return false;
      if (filterSearch) {
        const q = filterSearch.toLowerCase();
        const etab = etablissements.find(e => e.id === adh.etablissementId)?.nom || '';
        if (!(adh.prenom+' '+adh.nom+' '+adh.metier+' '+etab+' '+adh.numero).toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [adherents, etablissements, filterSearch, filterStatut, filterEtab, filterPay]); // eslint-disable-line

  const etabName = (id) => etablissements.find(e => e.id === id)?.nom || '—';
  const isAdminRole = currentRole === 'tresorier';
  const canSeeRib = isAdminRole;
  const canDelete = isAdminRole;
  const canImportExport = isAdminRole;

  const startEdit = (adh) => {
    setForm({ ...EMPTY_ADH, ...adh });
    setMetierSearch(adh.metier || '');
    setEditing(true);
    setCreating(false);
  };

  const startCreate = () => {
    const year = new Date().getFullYear();
    const seq = String(adherents.length + 1).padStart(3, '0');
    const nextNum = `FO-${year}-${seq}`;
    setForm({ ...EMPTY_ADH, id: '', numero: nextNum, createdBy: currentUsername || '' });
    setMetierSearch('');
    setEditing(true);
    setCreating(true);
    setSelectedId(null);
    setActiveTab('emploi');
  };

  const cancelEdit = () => { setEditing(false); setCreating(false); };

  const handleSave = () => {
    if (!form.prenom.trim() || !form.nom.trim()) return;
    const toSave = { ...form, metier: metierSearch };
    if (!toSave.id) toSave.id = uid();
    saveAdherent(toSave);
    setEditing(false);
    setCreating(false);
    setSelectedId(toSave.id);
  };

  const handleDelete = (id) => {
    if (!window.confirm('Supprimer cet adhérent ?')) return;
    deleteAdherent(id);
    setSelectedId(null);
    setEditing(false);
  };

  const handleNewEtab = () => {
    if (!newEtabNom.trim()) return;
    const etab = { id: uid(), nom: newEtabNom.trim(), adresse:'', telephone:'', email:'', delegue:'', notes:'' };
    saveEtablissement(etab);
    setForm(f => ({ ...f, etablissementId: etab.id }));
    setShowNewEtab(false);
    setNewEtabNom('');
  };

  // Adherent history from cotisations
  const history = useMemo(() => {
    if (!selected) return [];
    const entries = [];
    Object.entries(cotisations).forEach(([year, yearData]) => {
      const adhData = yearData[selected.id] || {};
      Object.entries(adhData).forEach(([month, c]) => {
        if (c.status !== 'non_pointe' && c.status !== 'paye') {
          entries.push({ year: +year, month: +month, ...c });
        }
      });
    });
    return entries.sort((a, b) => b.year - a.year || b.month - a.month);
  }, [selected, cotisations]);

  // Total paid this year
  const yearPaid = useMemo(() => {
    if (!selected) return 0;
    const y = cotisations[selectedYear] || {};
    return Object.values(y[selected.id] || {}).reduce((s, c) => s + (c.status === 'paye' ? (selected.montant || 0) : 0), 0);
  }, [selected, cotisations, selectedYear]);

  // CSV import
  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportError('');
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target.result;
        const lines = text.split('\n').filter(l => l.trim());
        if (lines.length < 2) { setImportError('Fichier vide ou invalide.'); return; }
        const header = lines[0].split(/[;,]/).map(h => h.trim().replace(/"/g,'').toLowerCase());
        const idx = (keys) => { for (const k of keys) { const i = header.indexOf(k); if (i >= 0) return i; } return -1; };
        const prenomIdx = idx(['prenom','prénom','firstname','first_name']);
        const nomIdx = idx(['nom','lastname','last_name','name']);
        if (prenomIdx < 0 || nomIdx < 0) { setImportError('Colonnes "prénom" et "nom" requises.'); return; }
        const etabIdx = idx(['etablissement','établissement','structure','etab']);
        const metierIdx = idx(['metier','métier','poste','fonction']);
        const contratIdx = idx(['contrat','type_contrat']);
        const modeIdx = idx(['mode_paiement','mode','paiement']);
        const montantIdx = idx(['montant','cotisation']);
        let count = 0;
        lines.slice(1).forEach(line => {
          const cols = line.split(/[;,]/).map(c => c.trim().replace(/^"|"$/g,''));
          const prenom = cols[prenomIdx] || '';
          const nom = cols[nomIdx] || '';
          if (!prenom || !nom) return;
          let etabId = '';
          if (etabIdx >= 0 && cols[etabIdx]) {
            const etabNom = cols[etabIdx];
            const found = etablissements.find(e => e.nom.toLowerCase() === etabNom.toLowerCase());
            if (found) etabId = found.id;
            else {
              const newE = { id: uid(), nom: etabNom, adresse:'', telephone:'', email:'', delegue:'', notes:'' };
              saveEtablissement(newE);
              etabId = newE.id;
            }
          }
          const nextNum = 'FO-' + String(adherents.length + count + 1).padStart(3,'0');
          const adh = {
            ...EMPTY_ADH, id: uid(),
            prenom, nom, numero: nextNum, statut: 'Actif',
            etablissementId: etabId,
            metier: metierIdx >= 0 ? (cols[metierIdx] || '') : '',
            contrat: contratIdx >= 0 ? (cols[contratIdx] || 'CDI') : 'CDI',
            modePaiement: modeIdx >= 0 ? (cols[modeIdx] || 'Prélèvement auto') : 'Prélèvement auto',
            montant: montantIdx >= 0 ? (parseFloat(cols[montantIdx])||0) : 0,
          };
          saveAdherent(adh);
          count++;
        });
        addJournal('import', `${count} adhérent(s) importé(s) depuis CSV`);
        setImportError(`✅ ${count} adhérent(s) importé(s) avec succès.`);
      } catch (err) {
        setImportError('Erreur lors de la lecture du fichier.');
      }
    };
    reader.readAsText(file, 'UTF-8');
    e.target.value = '';
  };

  const handleExport = () => {
    const rows = [['N°','Prénom','Nom','Statut','Établissement','Métier','Contrat','Convention','Coefficient','Date adhésion','Mode paiement','Périodicité','Montant/échéance','Téléphone','Email']];
    adherents.forEach(adh => {
      rows.push([adh.numero,adh.prenom,adh.nom,adh.statut,etabName(adh.etablissementId),adh.metier,adh.contrat,adh.convention,adh.coefficient,adh.dateAdhesion,adh.modePaiement,adh.periodicite,adh.montant,adh.telephone,adh.email]);
    });
    exportCSV(rows, 'adherents_sdassp.csv');
  };

  const F = ({ label, val }) => (
    <div style={{ marginBottom:12 }}>
      <div className="form-label">{label}</div>
      <div style={{ fontSize:14, color:'#222', padding:'6px 0' }}>{val || <span style={{color:'#bbb'}}>—</span>}</div>
    </div>
  );

  const f = (k) => (v) => setForm(prev => ({ ...prev, [k]: v }));

  const metierFiltered = METIERS_SUGGESTIONS.filter(m => m.toLowerCase().includes(metierSearch.toLowerCase()));

  // ── LIST VIEW ──
  if (!selectedId && !creating) {
    return (
      <div>
        <div className="page-header">
          <div>
            <div className="page-title">👥 Adhérents</div>
            <div className="page-subtitle">{adherents.length} adhérent{adherents.length>1?'s':''} · {adherents.filter(a=>a.statut==='Actif').length} actifs</div>
          </div>
          <div className="flex gap-2">
            {canImportExport && <button className="btn btn-outline btn-sm" onClick={handleExport}>⬇️ CSV</button>}
            {canImportExport && <button className="btn btn-outline btn-sm" onClick={() => fileRef.current?.click()}>⬆️ Import CSV</button>}
            <input ref={fileRef} type="file" accept=".csv,.txt" style={{display:'none'}} onChange={handleImport} />
            {isAdminRole && <button className="btn btn-primary" onClick={startCreate}>+ Nouvel adhérent</button>}
          </div>
        </div>

        {importError && <div className={`alert ${importError.startsWith('✅')?'alert-success':'alert-danger'} mb-3`}>{importError}</div>}

        <div className="card mb-4">
          <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:10 }}>
            <input className="form-control" style={{ flex:1, minWidth:160 }} placeholder="🔍 Recherche..."
              value={filterSearch} onChange={e => setFilterSearch(e.target.value)} />
            <select className="form-control" style={{ width:130 }} value={filterStatut} onChange={e => setFilterStatut(e.target.value)}>
              <option value="">Tous statuts</option>
              {STATUTS_ADHESION.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className="form-control" style={{ width:190 }} value={filterEtab} onChange={e => setFilterEtab(e.target.value)}>
              <option value="">Tous établissements</option>
              {[...etablissements].sort((a,b) => (a.nom||'').localeCompare(b.nom||'')).map(e => <option key={e.id} value={e.id}>{e.nom}</option>)}
            </select>
            {(filterSearch||filterStatut||filterEtab||filterPay) && (
              <button className="btn btn-ghost btn-sm" onClick={() => { setFilterSearch(''); setFilterStatut(''); setFilterEtab(''); setFilterPay(''); }}>✕ Effacer</button>
            )}
          </div>
          <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
            {PAY_OPTIONS.map(p => {
              const active = filterPay === p.id;
              const cnt = p.test ? adherents.filter(p.test).length : adherents.length;
              if (p.id !== '' && cnt === 0) return null;
              return (
                <button key={p.id} onClick={() => setFilterPay(p.id)} style={{
                  padding:'4px 11px', borderRadius:20, border:'none', cursor:'pointer',
                  background: active ? '#CC0000' : '#f0f0f0',
                  color: active ? '#fff' : '#555',
                  fontWeight: active ? 700 : 500, fontSize:12, transition:'all .15s',
                  display:'flex', alignItems:'center', gap:5
                }}>
                  {p.label}
                  {p.id !== '' && <span style={{ background: active?'rgba(255,255,255,.3)':'#ddd', color: active?'#fff':'#777', borderRadius:10, fontSize:10, padding:'0 5px', fontWeight:700, lineHeight:'17px' }}>{cnt}</span>}
                </button>
              );
            })}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">👥</div>
            <div className="empty-state-text">Aucun adhérent trouvé.</div>
            <button className="btn btn-primary mt-3" onClick={startCreate}>+ Créer le premier adhérent</button>
          </div>
        ) : (
          <div>
            {filtered.map(adh => {
              const yearCots = cotisations[selectedYear] || {};
              const collected = Object.values(yearCots[adh.id] || {}).reduce((s,c) => s + (c.status==='paye'?(adh.montant||0):0), 0);
              const mult = adh.periodicite==='Trimestrielle'?4:adh.periodicite==='Semestrielle'?2:adh.periodicite==='Annuelle'?1:12;
              const total = (adh.montant||0) * mult;
              const dotClass = adh.statut==='Actif'?'actif':adh.statut==='Suspendu'?'suspendu':adh.statut==='Inactif'?'inactif':'radie';
              return (
                <div key={adh.id} className="member-card" onClick={() => { setSelectedId(adh.id); setEditing(false); setActiveTab('emploi'); }}>
                  <div className="member-card-avatar">{initials(adh.prenom, adh.nom)}</div>
                  <div className="member-card-info">
                    <div className="member-card-name">
                      {adh.prenom} {adh.nom}
                      <span className="member-card-num">#{adh.numero}</span>
                    </div>
                    <div className="member-card-role">{adh.metier || '—'} · {etabName(adh.etablissementId)}</div>
                    <div style={{display:'flex',gap:4,flexWrap:'wrap',alignItems:'center',marginTop:2,position:'relative'}} onClick={e=>e.stopPropagation()}>
                      {(adh.mandats||[]).map(m => (
                        <span key={m}
                          title="Cliquer pour retirer ce mandat"
                          onClick={e => { e.stopPropagation(); const next=(adh.mandats||[]).filter(x=>x!==m); saveAdherent({...adh,mandats:next}); }}
                          style={{fontSize:10,fontWeight:700,background:'#fff3cd',color:'#856404',borderRadius:999,padding:'1px 7px',border:'1px solid #fde68a',cursor:'pointer',userSelect:'none'}}>
                          {m} ✕
                        </span>
                      ))}
                      <button
                        title="Ajouter / gérer les mandats"
                        onClick={e => { e.stopPropagation(); setMandatPopover(mandatPopover===adh.id?null:adh.id); }}
                        style={{fontSize:10,fontWeight:700,background:'#f0f9ff',color:'#0369a1',borderRadius:999,padding:'1px 8px',border:'1px solid #bae6fd',cursor:'pointer',lineHeight:'18px'}}>
                        + Mandat
                      </button>
                      {mandatPopover === adh.id && (
                        <div style={{
                          position:'absolute',top:'100%',left:0,zIndex:500,
                          background:'#fff',border:'1px solid #e5e7eb',borderRadius:10,
                          boxShadow:'0 8px 24px rgba(0,0,0,.12)',padding:'10px 12px',
                          minWidth:240,marginTop:4,
                        }}>
                          <div style={{fontSize:11,fontWeight:700,color:'#888',marginBottom:6,textTransform:'uppercase',letterSpacing:'.5px'}}>Mandats syndicaux</div>
                          <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
                            {MANDATS.map(m => {
                              const sel=(adh.mandats||[]).includes(m);
                              return (
                                <button key={m} type="button"
                                  onClick={e => { e.stopPropagation(); const next=sel?(adh.mandats||[]).filter(x=>x!==m):[...(adh.mandats||[]),m]; saveAdherent({...adh,mandats:next}); }}
                                  style={{fontSize:11,fontWeight:700,borderRadius:999,padding:'4px 11px',cursor:'pointer',border:'1.5px solid',
                                    background:sel?'#fef08a':'#f9fafb',color:sel?'#713f12':'#6b7280',borderColor:sel?'#facc15':'#e5e7eb',
                                  }}>{sel?`✓ ${m}`:m}</button>
                              );
                            })}
                          </div>
                          <button onClick={e=>{e.stopPropagation();setMandatPopover(null);}} style={{marginTop:8,fontSize:11,color:'#aaa',background:'none',border:'none',cursor:'pointer',width:'100%',textAlign:'right'}}>Fermer</button>
                        </div>
                      )}
                    </div>
                    <div className="member-card-pay">
                      {adh.modePaiement === 'Prélèvement auto' ? `Prélèv. le ${adh.jourPrelevement||'?'}` : adh.modePaiement}
                      {adh.periodicite ? ` · ${adh.periodicite}` : ''}
                      {adh.montant ? ` · ${Number(adh.montant).toFixed(2)} €` : ''}
                      {adh.contrat ? ` · ${adh.contrat}` : ''}
                    </div>
                  </div>
                  <div className="member-card-right">
                    <div className="member-card-amount">{Math.round(collected)}<span style={{fontWeight:400,color:'#aaa',fontSize:11}}>/{Math.round(total)} €</span></div>
                    <div style={{fontSize:12,color:'#666',marginTop:4}}>
                      <span className={`status-dot ${dotClass}`}/>{adh.statut}
                    </div>
                  </div>
                  <div style={{display:'flex',flexDirection:'column',gap:4,marginLeft:4}} onClick={e=>e.stopPropagation()}>
                    <button className="btn-icon" style={{fontSize:14}} onClick={() => { startEdit(adh); setSelectedId(adh.id); setActiveTab('emploi'); }}>✏️</button>
                    {canDelete && <button className="btn-icon" style={{fontSize:14,color:'#e57373'}} onClick={() => handleDelete(adh.id)}>🗑️</button>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── FORM / DETAIL VIEW ──
  const adh = creating ? form : (selected || form);
  const isView = !editing;

  const currentIdx = creating ? -1 : filtered.findIndex(a => a.id === selectedId);
  const prevAdh = currentIdx > 0 ? filtered[currentIdx - 1] : null;
  const nextAdh = currentIdx >= 0 && currentIdx < filtered.length - 1 ? filtered[currentIdx + 1] : null;
  const navTo = (a) => { setSelectedId(a.id); setEditing(false); setActiveTab('emploi'); };

  return (
    <div>
      <div className="page-header">
        <div className="flex items-center gap-3">
          <button className="btn btn-ghost" onClick={() => { setSelectedId(null); setEditing(false); setCreating(false); }}>← Retour</button>
          {!creating && (
            <div style={{ display:'flex', gap:4 }}>
              <button
                onClick={() => prevAdh && navTo(prevAdh)}
                disabled={!prevAdh}
                title={prevAdh ? `${prevAdh.prenom} ${prevAdh.nom}` : ''}
                style={{ padding:'4px 10px', borderRadius:6, border:'1px solid #e0e0e0', background: prevAdh ? '#fff' : '#f5f5f5', color: prevAdh ? '#333' : '#ccc', cursor: prevAdh ? 'pointer' : 'default', fontSize:16, lineHeight:1 }}>
                ‹
              </button>
              <button
                onClick={() => nextAdh && navTo(nextAdh)}
                disabled={!nextAdh}
                title={nextAdh ? `${nextAdh.prenom} ${nextAdh.nom}` : ''}
                style={{ padding:'4px 10px', borderRadius:6, border:'1px solid #e0e0e0', background: nextAdh ? '#fff' : '#f5f5f5', color: nextAdh ? '#333' : '#ccc', cursor: nextAdh ? 'pointer' : 'default', fontSize:16, lineHeight:1 }}>
                ›
              </button>
              <span style={{ fontSize:11, color:'#aaa', alignSelf:'center', paddingLeft:2 }}>
                {currentIdx + 1} / {filtered.length}
              </span>
            </div>
          )}
          <div>
            <div className="page-title">
              {creating ? 'Nouvel adhérent' : `${adh.prenom} ${adh.nom}`}
            </div>
            {!creating && <div className="page-subtitle">{adh.numero} · {etabName(adh.etablissementId)}</div>}
          </div>
        </div>
        <div className="flex gap-2">
          {isView && !creating && (
            <>
              <button className="btn btn-outline btn-sm" onClick={() => startEdit(adh)}>✏️ Modifier</button>
              {canDelete && <button className="btn btn-outline btn-sm" style={{ color:'#c62828', borderColor:'#ef9a9a' }} onClick={() => handleDelete(adh.id)}>🗑️ Supprimer</button>}
            </>
          )}
          {editing && (
            <>
              <button className="btn btn-outline" onClick={cancelEdit}>Annuler</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={!form.prenom||!form.nom}>💾 Enregistrer</button>
            </>
          )}
        </div>
      </div>

      {/* Identity header (always visible) */}
      <div className="card mb-4">
        <div style={{ display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' }}>
          <div className="adh-avatar" style={{ width:56, height:56, fontSize:20 }}>
            {initials(adh.prenom, adh.nom)}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            {editing ? (
              <div className="grid-2" style={{ gap:10 }}>
                <div>
                  <label className="form-label">Genre</label>
                  <select className="form-control" value={form.genre || ''} onChange={e => f('genre')(e.target.value)}>
                    <option value="">— Non renseigné —</option>
                    <option value="Madame">Madame</option>
                    <option value="Monsieur">Monsieur</option>
                  </select>
                </div>
                <div />
                <div><label className="form-label">Prénom *</label><input className="form-control" value={form.prenom} onChange={e => f('prenom')(e.target.value)} /></div>
                <div><label className="form-label">Nom *</label><input className="form-control" value={form.nom} onChange={e => f('nom')(e.target.value.toUpperCase())} /></div>
                <div><label className="form-label">N° adhérent</label><input className="form-control" value={form.numero} onChange={e => f('numero')(e.target.value)} /></div>
                <div>
                  <label className="form-label">Statut</label>
                  <select className="form-control" value={form.statut} onChange={e => f('statut')(e.target.value)}>
                    {STATUTS_ADHESION.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            ) : (
              <>
                <div style={{ fontSize:20, fontWeight:700 }}>
                  {adh.genre && <span style={{ fontSize:13, color:'#888', fontWeight:400, marginRight:6 }}>{adh.genre}</span>}
                  {adh.prenom} {adh.nom}
                </div>
                <div className="flex gap-3 mt-1" style={{ flexWrap:'wrap' }}>
                  <span style={{ fontSize:13, color:'#888' }}>{adh.numero}</span>
                  <span className={`badge badge-${adh.statut==='Actif'?'actif':adh.statut==='Suspendu'?'suspendu':adh.statut==='Inactif'?'inactif':'radie'}`}>{adh.statut}</span>
                  {!creating && <span style={{ fontSize:13, color:'#888' }}>Cotisé {selectedYear} : <strong>{formatMontant(yearPaid)}</strong></span>}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        {['emploi','cotisation','contact','documents','historique'].map(t => (
          <button key={t} className={`tab-btn${activeTab===t?' active':''}`} onClick={() => setActiveTab(t)}>
            {t === 'emploi' ? '💼 Emploi' : t === 'cotisation' ? '💰 Cotisation' : t === 'contact' ? '📞 Contact' : t === 'documents' ? `📎 Documents${(adh.documents||[]).length > 0 ? ` (${(adh.documents||[]).length})` : ''}` : '📋 Historique'}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="card">
        {activeTab === 'emploi' && (
          editing ? (
            <div>
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Établissement</label>
                  <div style={{ display:'flex', gap:6 }}>
                    <select className="form-control" value={form.etablissementId}
                      onChange={e => {
                        const etabId = e.target.value;
                        const etab = etablissements.find(x => x.id === etabId);
                        setForm(prev => ({ ...prev, etablissementId: etabId, delegueNom: etab?.delegue || prev.delegueNom }));
                      }}
                      style={{ flex:1 }}>
                      <option value="">— Sélectionner —</option>
                      {[...etablissements].sort((a,b) => (a.nom||'').localeCompare(b.nom||'')).map(e => <option key={e.id} value={e.id}>{e.nom}</option>)}
                    </select>
                    <button className="btn btn-outline btn-sm" onClick={() => setShowNewEtab(true)} title="Créer établissement">+</button>
                  </div>
                  {showNewEtab && (
                    <div style={{ display:'flex', gap:6, marginTop:6 }}>
                      <input className="form-control" placeholder="Nom établissement" value={newEtabNom} onChange={e => setNewEtabNom(e.target.value)} />
                      <button className="btn btn-primary btn-sm" onClick={handleNewEtab}>Créer</button>
                      <button className="btn btn-outline btn-sm" onClick={() => setShowNewEtab(false)}>✕</button>
                    </div>
                  )}
                </div>
                <div className="form-group autocomplete-wrap">
                  <label className="form-label">Métier / Poste</label>
                  <input className="form-control" value={metierSearch}
                    onChange={e => { setMetierSearch(e.target.value); setMetierOpen(true); }}
                    onFocus={() => setMetierOpen(true)}
                    onBlur={() => setTimeout(() => setMetierOpen(false), 150)}
                    placeholder="Ex : Aide-soignante, Infirmier..." />
                  {metierOpen && metierFiltered.length > 0 && metierSearch.length > 0 && (
                    <div className="autocomplete-list">
                      {metierFiltered.map(m => (
                        <div key={m} className="autocomplete-item" onMouseDown={() => { setMetierSearch(m); setMetierOpen(false); }}>{m}</div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Contrat</label>
                  <select className="form-control" value={form.contrat} onChange={e => f('contrat')(e.target.value)}>
                    {CONTRATS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Date d'entrée</label>
                  <input className="form-control" type="date" value={form.dateEntree} onChange={e => f('dateEntree')(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Convention collective</label>
                  <select className="form-control"
                    value={CONVENTIONS.slice(0,-1).includes(form.convention) ? form.convention : 'Autre'}
                    onChange={e => f('convention')(e.target.value === 'Autre' ? '' : e.target.value)}>
                    {CONVENTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {!CONVENTIONS.slice(0,-1).includes(form.convention) && (
                    <input className="form-control" style={{ marginTop: 6 }}
                      value={form.convention}
                      onChange={e => f('convention')(e.target.value)}
                      placeholder="Saisir la convention collective..." />
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Coefficient</label>
                  <input className="form-control" value={form.coefficient} onChange={e => f('coefficient')(e.target.value)} placeholder="Ex : 376" />
                </div>
                <div className="form-group">
                  <label className="form-label">Date d'adhésion FO</label>
                  <input className="form-control" type="date" value={form.dateAdhesion} onChange={e => f('dateAdhesion')(e.target.value)} />
                </div>
                <div className="form-group" style={{gridColumn:'1/-1'}}>
                  <label className="form-label">Mandats syndicaux</label>
                  <div style={{display:'flex',gap:6,flexWrap:'wrap',padding:'8px 0'}}>
                    {MANDATS.map(m => {
                      const sel = (form.mandats||[]).includes(m);
                      return (
                        <button key={m} type="button"
                          onClick={() => f('mandats')(sel ? (form.mandats||[]).filter(x=>x!==m) : [...(form.mandats||[]),m])}
                          style={{
                            fontSize:11,fontWeight:700,borderRadius:999,padding:'4px 12px',cursor:'pointer',border:'1.5px solid',
                            background:sel?'#fef08a':'#f9fafb',
                            color:sel?'#713f12':'#6b7280',
                            borderColor:sel?'#facc15':'#e5e7eb',
                          }}>{m}</button>
                      );
                    })}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Délégué syndical</label>
                  <input className="form-control" value={form.delegueNom} onChange={e => f('delegueNom')(e.target.value)} placeholder="Prénom NOM du délégué" />
                </div>
                <div className="form-group">
                  <label className="form-label">Créé par</label>
                  <input className="form-control" value={form.createdBy} readOnly style={{ background:'#f8f8f8', color:'#888' }} />
                </div>
              </div>
            </div>
          ) : (
            <div className="grid-2">
              <F label="Établissement" val={<span style={{cursor:'pointer',color:'#CC0000',textDecoration:'underline'}} onClick={() => goToEtab && goToEtab(adh.etablissementId)}>{etabName(adh.etablissementId)}</span>} />
              <F label="Métier / Poste" val={adh.metier} />
              <F label="Contrat" val={adh.contrat} />
              <F label="Date d'entrée" val={formatDate(adh.dateEntree)} />
              <F label="Convention collective" val={adh.convention} />
              <F label="Coefficient" val={adh.coefficient} />
              <F label="Date d'adhésion FO" val={formatDate(adh.dateAdhesion)} />
              <F label="Ancienneté FO" val={anciennete(adh.dateAdhesion)} />
              {(adh.mandats||[]).length > 0 && (
                <div style={{gridColumn:'1/-1'}}>
                  <F label="Mandats syndicaux" val={
                    <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                      {(adh.mandats||[]).map(m => (
                        <span key={m} style={{fontSize:11,fontWeight:700,background:'#fef08a',color:'#713f12',borderRadius:999,padding:'2px 10px',border:'1px solid #fde68a'}}>{m}</span>
                      ))}
                    </div>
                  } />
                </div>
              )}
              {adh.delegueNom && <F label="Délégué syndical" val={adh.delegueNom} />}
              {adh.createdBy && <F label="Créé par" val={adh.createdBy} />}
            </div>
          )
        )}

        {activeTab === 'cotisation' && (
          editing ? (
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Mode de paiement</label>
                <select className="form-control" value={form.modePaiement} onChange={e => f('modePaiement')(e.target.value)}>
                  {MODES_PAIEMENT.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              {form.modePaiement === 'Prélèvement auto' && (
                <div className="form-group">
                  <label className="form-label">Jour de prélèvement</label>
                  <select className="form-control" value={form.jourPrelevement} onChange={e => f('jourPrelevement')(e.target.value)}>
                    {JOURS_PRELEVEMENT.map(j => <option key={j} value={j}>{j === '1' ? '1er' : j} du mois</option>)}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Périodicité</label>
                <select className="form-control" value={form.periodicite} onChange={e => {
                  const val = e.target.value;
                  setForm(p => ({ ...p, periodicite: val, ...(val !== 'Annuelle' ? { moisEcheanceAnnuelle: null } : {}) }));
                }}>
                  {PERIODICITES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              {form.periodicite === 'Annuelle' && (
                <div className="form-group">
                  <label className="form-label">Mois d'échéance annuelle</label>
                  <select className="form-control" value={form.moisEcheanceAnnuelle || ''}
                    onChange={e => setForm(p => ({ ...p, moisEcheanceAnnuelle: e.target.value ? Number(e.target.value) : null }))}>
                    <option value="">— Choisir un mois —</option>
                    {MOIS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                  </select>
                  {!form.moisEcheanceAnnuelle && (
                    <div style={{ fontSize: 12, color: '#e65100', marginTop: 4 }}>
                      ⚠️ Aucun mois d'échéance défini — janvier utilisé par défaut.
                    </div>
                  )}
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Montant / échéance (€)</label>
                {!isAdminRole ? (
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <input className="form-control" type="number" value={form.montant} readOnly
                      style={{ background:'#f5f5f5', color:'#888', cursor:'not-allowed' }} />
                    <span style={{ fontSize:11, color:'#e65100', whiteSpace:'nowrap' }}>🔒 Réservé bureau</span>
                  </div>
                ) : (
                  <input className="form-control" type="number" step="0.01" min="0" value={form.montant} onChange={e => f('montant')(parseFloat(e.target.value)||0)} />
                )}
              </div>
              {(form.modePaiement === 'Prélèvement auto' || form.modePaiement === 'Virement') && canSeeRib && (
                <>
                  <div className="form-group" style={{ gridColumn:'1/-1' }}>
                    <label className="form-label">IBAN / RIB</label>
                    <input className="form-control" value={form.rib} onChange={e => f('rib')(e.target.value)} placeholder="FR76 XXXX..." style={{ fontFamily:'monospace' }} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">BIC / SWIFT</label>
                    <input className="form-control" value={form.bic || ''} onChange={e => f('bic')(e.target.value.toUpperCase())} placeholder="Ex : AGRIFRPP" style={{ fontFamily:'monospace' }} />
                  </div>
                </>
              )}
              {(form.modePaiement === 'Prélèvement auto' || form.modePaiement === 'Virement') && !canSeeRib && (
                <div className="form-group" style={{ gridColumn:'1/-1' }}>
                  <div style={{ background:'#fff8e1', border:'1px solid #ffe082', borderRadius:6, padding:'8px 12px', fontSize:13, color:'#f57f17' }}>
                    🏦 Pour modifier l'IBAN/RIB, utilisez le bouton « Demande de changement RIB » dans la fiche.
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="grid-2">
                <F label="Mode de paiement" val={adh.modePaiement} />
                {adh.modePaiement === 'Prélèvement auto' && (
                  <F label="Jour de prélèvement" val={adh.jourPrelevement ? `${adh.jourPrelevement === '1' ? '1er' : adh.jourPrelevement} du mois` : '—'} />
                )}
                <F label="Périodicité" val={adh.periodicite} />
                <F label="Montant / échéance" val={adh.montant ? formatMontant(adh.montant) : '—'} />
              </div>
              {adh.rib && canSeeRib && (
                <div style={{ gridColumn:'1/-1', display:'flex', gap:16, flexWrap:'wrap' }}>
                  <div>
                    <div className="form-label" style={{ marginBottom:4 }}>IBAN / RIB</div>
                    <code style={{ fontSize:13, background:'#f5f5f5', padding:'4px 8px', borderRadius:4, display:'inline-block' }}>{adh.rib}</code>
                  </div>
                  {adh.bic && (
                    <div>
                      <div className="form-label" style={{ marginBottom:4 }}>BIC / SWIFT</div>
                      <code style={{ fontSize:13, background:'#f5f5f5', padding:'4px 8px', borderRadius:4, display:'inline-block' }}>{adh.bic}</code>
                    </div>
                  )}
                </div>
              )}
              {!canSeeRib && adh.rib && (
                <div style={{ gridColumn:'1/-1', background:'#fff8e1', border:'1px solid #ffe082', borderRadius:6, padding:'8px 12px', fontSize:12, color:'#f57f17' }}>
                  🏦 Informations bancaires — utilisez « Demander changement de RIB » pour modifier
                </div>
              )}
              {/* Year cotisation summary */}
              <div className="divider" />
              <div style={{ fontSize:14, fontWeight:600, marginBottom:10 }}>Pointage {selectedYear}</div>
              <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                {MOIS.map((m, mi) => {
                  const c = (((cotisations[selectedYear] || {})[adh.id]) || {})[mi+1];
                  const s = c ? c.status : 'non_pointe';
                  const colors = { paye:'#2e7d32', defaut_provision:'#e65100', arret_adhesion:'#6a1b9a', autre_motif:'#555', non_pointe:'#bbb' };
                  const bgs = { paye:'#e8f5e9', defaut_provision:'#fff3e0', arret_adhesion:'#f3e5f5', autre_motif:'#f5f5f5', non_pointe:'#f5f5f5' };
                  return (
                    <div key={mi} title={`${m}: ${c?.motif ? c.motif : ''}`} style={{ textAlign:'center', width:44 }}>
                      <div style={{ fontSize:10, color:'#888', marginBottom:2 }}>{m.slice(0,3)}</div>
                      <div style={{ background:bgs[s], color:colors[s], borderRadius:4, padding:'3px 0', fontSize:11, fontWeight:600 }}>
                        {s === 'paye' ? '✓' : s === 'non_pointe' ? '–' : s === 'defaut_provision' ? 'DP' : s === 'arret_adhesion' ? 'AA' : 'AM'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )
        )}

        {activeTab === 'contact' && (
          editing ? (
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Téléphone</label>
                <input className="form-control" type="tel" value={form.telephone} onChange={e => f('telephone')(e.target.value)} placeholder="0692 XX XX XX" />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-control" type="email" value={form.email} onChange={e => f('email')(e.target.value)} placeholder="prenom.nom@email.re" />
              </div>
              <div className="form-group" style={{ gridColumn:'1/-1' }}>
                <label className="form-label">Adresse</label>
                <input className="form-control" value={form.adresse} onChange={e => f('adresse')(e.target.value)} placeholder="Rue, CP Ville" />
              </div>
              <div className="form-group" style={{ gridColumn:'1/-1' }}>
                <label className="form-label">Notes</label>
                <textarea className="form-control" value={form.notes} onChange={e => f('notes')(e.target.value)} rows={3} />
              </div>
            </div>
          ) : (
            <div className="grid-2">
              <F label="Téléphone" val={adh.telephone ? <a href={`tel:${adh.telephone}`} style={{color:'#CC0000'}}>{adh.telephone}</a> : null} />
              <F label="Email" val={adh.email ? <a href={`mailto:${adh.email}`} style={{color:'#CC0000'}}>{adh.email}</a> : null} />
              {adh.adresse && <div style={{ gridColumn:'1/-1' }}><F label="Adresse" val={adh.adresse} /></div>}
              {adh.notes && <div style={{ gridColumn:'1/-1' }}><F label="Notes" val={adh.notes} /></div>}
            </div>
          )
        )}

        {activeTab === 'documents' && (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
              <div style={{ fontSize:14, fontWeight:600 }}>Documents joints</div>
              {isAdminRole && <button className="btn btn-outline btn-sm" onClick={() => docRef.current?.click()}>📎 Ajouter un document</button>}
              <input ref={docRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.txt" style={{ display:'none' }}
                onChange={e => {
                  const file = e.target.files[0];
                  if (!file) return;
                  if (file.size > 5 * 1024 * 1024) { alert('Fichier trop volumineux (max 5 Mo)'); e.target.value=''; return; }
                  const reader = new FileReader();
                  reader.onload = ev => {
                    const doc = { id: uid(), nom: file.name, type: file.type, data: ev.target.result, dateAjout: new Date().toISOString() };
                    const updated = { ...adh, documents: [...(adh.documents||[]), doc] };
                    saveAdherent(updated);
                  };
                  reader.readAsDataURL(file);
                  e.target.value = '';
                }}
              />
            </div>
            {(adh.documents||[]).length === 0 ? (
              <div className="text-sm text-gray" style={{ padding:'20px 0', textAlign:'center' }}>Aucun document joint. Cliquez sur "Ajouter" pour joindre un PDF, image ou document Word.</div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {(adh.documents||[]).map(doc => {
                  const icon = doc.type?.startsWith('image/') ? '🖼️' : doc.nom?.endsWith('.pdf') ? '📄' : doc.nom?.match(/\.docx?$/) ? '📝' : '📎';
                  return (
                    <div key={doc.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 12px', background:'#f9f9f9', borderRadius:8, border:'1px solid #eee' }}>
                      <span style={{ fontSize:22 }}>{icon}</span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontWeight:600, fontSize:13, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{doc.nom}</div>
                        <div style={{ fontSize:11, color:'#aaa' }}>{new Date(doc.dateAjout).toLocaleDateString('fr-FR')}</div>
                      </div>
                      {isAdminRole && <a href={doc.url || doc.data} download={doc.nom} target="_blank" rel="noreferrer" className="btn btn-outline btn-sm">⬇️ Télécharger</a>}
                      {isAdminRole && (
                        <button className="btn btn-outline btn-sm" style={{ color:'#c62828', borderColor:'#ef9a9a' }}
                          onClick={() => { if (window.confirm('Supprimer ce document ?')) { saveAdherent({ ...adh, documents: (adh.documents||[]).filter(d => d.id !== doc.id) }); } }}>
                          🗑️
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'historique' && (
          <div>
            <div style={{ fontSize:14, fontWeight:600, marginBottom:12 }}>Historique des impayés et motifs</div>
            {history.length === 0 ? (
              <div className="text-sm text-gray">Aucun impayé enregistré.</div>
            ) : (
              <div>
                {history.map((h, i) => (
                  <div key={i} style={{ display:'flex', gap:12, padding:'8px 0', borderBottom:'1px solid #f0f0f0', alignItems:'center' }}>
                    <div style={{ fontSize:13, fontWeight:600, width:100, flexShrink:0 }}>
                      {MOIS[h.month-1]} {h.year}
                    </div>
                    <span className={`badge ${STATUS_BADGE_CLASS[h.status]}`}>{STATUS_LABELS[h.status]}</span>
                    <div style={{ fontSize:13, color:'#555' }}>{h.motif || <span className="text-gray">Aucun motif précisé</span>}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Relance template */}
            {history.length > 0 && (
              <div className="mt-4">
                <div className="divider" />
                <details>
                  <summary style={{ cursor:'pointer', fontWeight:600, fontSize:14, color:'#CC0000', padding:'8px 0' }}>
                    📧 Modèle de courrier de relance
                  </summary>
                  <div style={{ background:'#f9f9f9', border:'1px solid #eee', borderRadius:8, padding:16, marginTop:8, fontSize:13, lineHeight:1.7, whiteSpace:'pre-wrap' }}>
{`Objet : Régularisation de cotisation syndicale FO

Madame, Monsieur ${adh.nom} ${adh.prenom},

Nous vous contactons au sujet de votre adhésion au Syndicat Départemental de l'Action Sociale et de la Santé Privée FO Réunion (SDASSP FO 974).

Il ressort de notre suivi qu'un ou plusieurs versements de cotisation sont actuellement en attente de régularisation${history.slice(0,2).map(h => ` (${MOIS[h.month-1]} ${h.year}${h.motif ? ' : '+h.motif : ''})`).join('')}.

Le montant mensuel de votre cotisation est de ${formatMontant(adh.montant)}, à régler par ${adh.modePaiement}.

Nous vous remercions de bien vouloir régulariser cette situation dans les meilleurs délais.

Pour toute question, n'hésitez pas à nous contacter à l'adresse : sdassp.fo974@gmail.com

Cordialement,
Le Bureau du SDASSP FO 974`}
                  </div>
                  <button className="btn btn-outline btn-sm mt-2" onClick={() => {
                    const text = `Objet : Régularisation de cotisation syndicale FO\n\nMadame, Monsieur ${adh.nom} ${adh.prenom},...`;
                    navigator.clipboard?.writeText(text);
                  }}>📋 Copier</button>
                </details>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Demandes spéciales */}
      {!editing && !creating && saveDemande && (
        <div className="card mt-4" style={{ border:'1px solid #ffe082', background:'#fffde7' }}>
          <div style={{ fontSize:14, fontWeight:700, marginBottom:10, color:'#555' }}>⚡ Demandes spéciales</div>
          <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
            <button className="btn btn-outline btn-sm" style={{ color:'#c62828', borderColor:'#ef9a9a' }}
              onClick={() => { setDemandeModal('arret'); setDemandeNotes(''); }}>
              🛑 Demander arrêt d'adhésion
            </button>
            <button className="btn btn-outline btn-sm" style={{ color:'#1565c0', borderColor:'#90caf9' }}
              onClick={() => { setDemandeModal('rib'); setNewIban(''); setNewBic(''); setDemandeNotes(''); }}>
              🏦 Demander changement de RIB
            </button>
            {adh.modePaiement !== 'Prélèvement auto' && (
              <button className="btn btn-outline btn-sm" style={{ color:'#2e7d32', borderColor:'#a5d6a7' }}
                onClick={() => {
                  const seq = (adherents.length || 0) + 1;
                  setBasculeIban(''); setBasculeBic(''); setBasculeTitulaire('');
                  setBasculeJour('5'); setBasculePeriodicite('Mensuelle'); setBasculeSepa(null);
                  setBasculeRum(generateRUM(adh.nom, seq));
                  setDemandeModal('bascule');
                }}>
                🔄 Passer en prélèvement automatique
              </button>
            )}
          </div>
          {(demandes||[]).some(d => d.adherentId === adh.id && d.statut === 'en_attente') && (
            <div style={{ marginTop:10, fontSize:12, color:'#CC0000', fontStyle:'italic' }}>
              ⏳ Une demande est en attente de traitement par le trésorier.
            </div>
          )}
        </div>
      )}

      {/* Modal arrêt adhésion */}
      {demandeModal === 'arret' && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth:420 }}>
            <div className="modal-header">
              <span className="modal-title">🛑 Demander l'arrêt d'adhésion</span>
              <button className="btn-icon" onClick={() => setDemandeModal(null)}>✕</button>
            </div>
            <div style={{ fontSize:13, color:'#555', marginBottom:12 }}>
              Adhérent : <strong>{adh.prenom} {adh.nom}</strong>
            </div>
            <div className="form-group">
              <label className="form-label">Motif / Raison *</label>
              <textarea className="form-control" rows={4} value={demandeNotes} onChange={e => setDemandeNotes(e.target.value)}
                placeholder="Ex : Départ en retraite, changement d'employeur, demande de l'adhérent..." />
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setDemandeModal(null)}>Annuler</button>
              <button className="btn btn-primary" style={{ background:'#c62828' }}
                disabled={!demandeNotes.trim()}
                onClick={() => {
                  saveDemande({ id: uid(), type: 'arret_adhesion', statut: 'en_attente',
                    adherentId: adh.id, adherentNom: adh.nom, adherentPrenom: adh.prenom,
                    motif: demandeNotes, createdBy: currentUsername || '', dateCreation: new Date().toISOString() });
                  addJournal && addJournal('demande', `Arrêt adhésion demandé : ${adh.prenom} ${adh.nom}`);
                  setDemandeModal(null);
                }}>
                Envoyer la demande
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal changement RIB */}
      {demandeModal === 'rib' && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth:460 }}>
            <div className="modal-header">
              <span className="modal-title">🏦 Demander un changement de RIB</span>
              <button className="btn-icon" onClick={() => setDemandeModal(null)}>✕</button>
            </div>
            <div style={{ fontSize:13, color:'#555', marginBottom:12 }}>
              Adhérent : <strong>{adh.prenom} {adh.nom}</strong>
            </div>
            <div className="form-group">
              <label className="form-label">Nouveau IBAN *</label>
              <input className="form-control" value={newIban} onChange={e => setNewIban(e.target.value.toUpperCase())}
                placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX" style={{ fontFamily:'monospace' }} />
            </div>
            <div className="form-group">
              <label className="form-label">Nouveau BIC / SWIFT</label>
              <input className="form-control" value={newBic} onChange={e => setNewBic(e.target.value.toUpperCase())}
                placeholder="Ex : AGRIFRPP" style={{ fontFamily:'monospace' }} />
            </div>
            <div className="form-group">
              <label className="form-label">📎 Pièce jointe RIB (optionnel)</label>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:4 }}>
                <button className="btn btn-outline btn-sm" onClick={() => ribDocRef.current?.click()}>
                  {ribDoc ? `✅ ${ribDoc.name}` : '📎 Joindre le RIB'}
                </button>
                {ribDoc && <button className="btn btn-outline btn-sm" style={{ color:'#c62828' }} onClick={() => setRibDoc(null)}>🗑️</button>}
              </div>
              <input ref={ribDocRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display:'none' }}
                onChange={e => { const f = e.target.files[0]; if (!f) return; const r = new FileReader(); r.onload = ev => setRibDoc({ name: f.name, type: f.type, data: ev.target.result }); r.readAsDataURL(f); }} />
            </div>
            <div className="form-group">
              <label className="form-label">Notes (optionnel)</label>
              <textarea className="form-control" rows={2} value={demandeNotes} onChange={e => setDemandeNotes(e.target.value)}
                placeholder="Raison du changement..." />
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => { setDemandeModal(null); setRibDoc(null); }}>Annuler</button>
              <button className="btn btn-primary"
                disabled={newIban.trim().length < 14}
                onClick={() => {
                  saveDemande({ id: uid(), type: 'changement_rib', statut: 'en_attente',
                    adherentId: adh.id, adherentNom: adh.nom, adherentPrenom: adh.prenom,
                    newIban: newIban.trim(), newBic: newBic.trim(), motif: demandeNotes,
                    ribDocument: ribDoc || null,
                    createdBy: currentUsername || '', dateCreation: new Date().toISOString() });
                  addJournal && addJournal('demande', `Changement RIB demandé : ${adh.prenom} ${adh.nom}`);
                  notifAdmins(
                    `🏦 Changement RIB — ${adh.prenom} ${adh.nom}`,
                    `Une demande de changement de RIB a été soumise par ${currentUsername || 'un délégué'}.

Adhérent : ${adh.prenom} ${adh.nom} (${adh.numero})
Nouveau IBAN : ${newIban.trim()}
Nouveau BIC : ${newBic.trim() || '—'}

Connectez-vous pour valider cette demande.`
                  );
                  setDemandeModal(null); setRibDoc(null);
                }}>
                Envoyer la demande
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal bascule → prélèvement auto */}
      {demandeModal === 'bascule' && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth:520 }}>
            <div className="modal-header">
              <span className="modal-title">🔄 Passage en prélèvement automatique</span>
              <button className="btn-icon" onClick={() => setDemandeModal(null)}>✕</button>
            </div>
            <div style={{ fontSize:13, color:'#555', marginBottom:4 }}>
              Adhérent : <strong>{adh.prenom} {adh.nom}</strong> — actuellement en <em>{adh.modePaiement}</em>
            </div>
            <div style={{ background:'#e8f5e9', border:'1px solid #a5d6a7', borderRadius:6, padding:'8px 12px', fontSize:12, color:'#2e7d32', marginBottom:14 }}>
              ✅ Une fois validée, cette demande mettra à jour le mode de paiement sans créer de doublon.
            </div>

            <div className="grid-2" style={{ gap:12 }}>
              <div className="form-group" style={{ gridColumn:'1/-1' }}>
                <label className="form-label">RUM (référence unique du mandat)</label>
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                  <input className="form-control" value={basculeRum} onChange={e => setBasculeRum(e.target.value)}
                    style={{ fontFamily:'monospace', fontWeight:600, color:'#CC0000' }} />
                  <button className="btn btn-outline btn-sm" title="Copier le RUM"
                    onClick={() => navigator.clipboard?.writeText(basculeRum)}>📋</button>
                </div>
              </div>
              <div className="form-group" style={{ gridColumn:'1/-1' }}>
                <label className="form-label">Titulaire du compte</label>
                <input className="form-control" value={basculeTitulaire} onChange={e => setBasculeTitulaire(e.target.value)}
                  placeholder="Prénom NOM du titulaire" />
              </div>
              <div className="form-group" style={{ gridColumn:'1/-1' }}>
                <label className="form-label">Nouveau IBAN *</label>
                <input className="form-control" value={basculeIban} onChange={e => setBasculeIban(e.target.value.toUpperCase())}
                  placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX" style={{ fontFamily:'monospace' }} />
              </div>
              <div className="form-group">
                <label className="form-label">BIC / SWIFT *</label>
                <input className="form-control" value={basculeBic} onChange={e => setBasculeBic(e.target.value.toUpperCase())}
                  placeholder="Ex : AGRIFRPP" style={{ fontFamily:'monospace' }} />
              </div>
              <div className="form-group">
                <label className="form-label">Jour de prélèvement</label>
                <select className="form-control" value={basculeJour} onChange={e => setBasculeJour(e.target.value)}>
                  {JOURS_PRELEVEMENT.map(j => <option key={j} value={j}>{j === '1' ? '1er' : j} du mois</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Périodicité</label>
                <select className="form-control" value={basculePeriodicite} onChange={e => setBasculePeriodicite(e.target.value)}>
                  {PERIODICITES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ gridColumn:'1/-1' }}>
                <label className="form-label">Mandat SEPA (PDF ou image)</label>
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                  <button className="btn btn-outline btn-sm" onClick={() => sepaRef.current?.click()}>
                    📎 {basculeSepa ? '✅ Document chargé' : 'Joindre le SEPA'}
                  </button>
                  {basculeSepa && <span style={{ fontSize:12, color:'#2e7d32' }}>{basculeSepa.nom}</span>}
                </div>
                <input ref={sepaRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display:'none' }}
                  onChange={e => {
                    const file = e.target.files[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = ev => setBasculeSepa({ nom: file.name, type: file.type, data: ev.target.result });
                    reader.readAsDataURL(file);
                    e.target.value = '';
                  }} />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setDemandeModal(null)}>Annuler</button>
              <button className="btn btn-primary" style={{ background:'#2e7d32' }}
                disabled={basculeIban.trim().length < 14 || !basculeBic.trim()}
                onClick={() => {
                  saveDemande({ id: uid(), type: 'bascule_prelevement', statut: 'en_attente',
                    adherentId: adh.id, adherentNom: adh.nom, adherentPrenom: adh.prenom,
                    newIban: basculeIban.trim(), newBic: basculeBic.trim(),
                    rum: basculeRum, titulaire: basculeTitulaire,
                    jourPrelevement: basculeJour, periodicite: basculePeriodicite,
                    sepaDocument: basculeSepa,
                    modePaiementActuel: adh.modePaiement,
                    createdBy: currentUsername || '', dateCreation: new Date().toISOString() });
                  addJournal && addJournal('demande', `Bascule prélèvement demandée : ${adh.prenom} ${adh.nom}`);
                  notifAdmins(
                    `🔄 Bascule prélèvement — ${adh.prenom} ${adh.nom}`,
                    `Une demande de passage en prélèvement automatique a été soumise par ${currentUsername || 'un délégué'}.\n\nAdhérent : ${adh.prenom} ${adh.nom} (${adh.numero})\nMode actuel : ${adh.modePaiement}\nNouveau IBAN : ${basculeIban.trim()}\nRUM : ${basculeRum}\n\nConnectez-vous pour valider cette demande.`
                  );
                  setDemandeModal(null);
                }}>
                🔄 Soumettre la demande de bascule
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
